import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

const getEnvironmentConfig = () => {
  // You can expand this based on your deployment setup
  const env = process.env.REACT_APP_ENVIRONMENT || 'development';

  if (env === 'prod') {
    return {
      environment: 'production',
      tracesSampleRate: 0.1,
      debug: false,
    };
  }

  if (env === 'staging') {
    return {
      environment: 'staging',
      tracesSampleRate: 0.5,
      debug: true,
    };
  }

  return {
    environment: 'development',
    tracesSampleRate: 1.0,
    debug: true,
  };
};

export const initializeSentry = () => {
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not found. Error tracking is disabled.');
    return;
  }

  const envConfig = getEnvironmentConfig();

  Sentry.init({
    dsn: SENTRY_DSN,
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    replaysSessionSampleRate: 0.1, // 10% of sessions get replay even without errors
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Filter out Facebook in-app browser (WebView) noise
      const message = event.exception?.values?.[0]?.value || '';
      if (message.includes('Java object is gone')) {
        return null;
      }
      // Filter out third-party script errors and Facebook autofill bridge crashes
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];

      // Facebook's autofill bridge injects JS that crashes on form pages.
      // frame.function is minified in beforeSend (sourcemaps apply server-side),
      // so also check the raw error stack string which keeps the original name.
      const rawStack = hint?.originalException?.stack || '';
      const isFacebookAutofill =
        rawStack.includes('setContactAutofillValuesFromBridge') ||
        frames.some((frame) =>
          (frame.function || '').includes('setContactAutofillValuesFromBridge')
        );
      if (isFacebookAutofill) {
        return null;
      }

      // Pinterest, ad trackers, etc.
      const isThirdParty = frames.some((frame) => {
        const filename = frame.filename || '';
        return (
          filename.includes('pinimg.com') ||
          filename.includes('facebook.net') ||
          filename.includes('googletagmanager.com') ||
          filename.includes('google-analytics.com') ||
          filename.includes('tiktok.com') ||
          filename.includes('everflow.io')
        );
      });
      if (isThirdParty) {
        return null;
      }

      // Filter unhandled "Load failed" from third-party fetch requests (e.g. Pinterest)
      if (
        message === 'Load failed' &&
        event.exception?.values?.[0]?.mechanism?.type === 'onunhandledrejection'
      ) {
        return null;
      }

      // Attach user email from session if Sentry.setUser hasn't been called yet
      if (!event.user?.email) {
        const email = sessionStorage.getItem('user');
        if (email && email !== 'undefined') {
          event.user = { ...event.user, email };
        }
      }

      // Enrich Axios network errors with request details
      const error = hint?.originalException;
      if (error?.isAxiosError) {
        event.tags = {
          ...event.tags,
          axios_url: error.config?.url,
          axios_method: error.config?.method?.toUpperCase(),
          axios_error_code: error.code,
        };
        event.extra = {
          ...event.extra,
          request_url: error.config?.url,
          request_method: error.config?.method?.toUpperCase(),
          request_data: error.config?.data,
          response_status: error.response?.status,
          response_data: error.response?.data,
          error_code: error.code,
          retry_count: error.config?.__retryCount || 0,
        };
      }

      // Attach network connection info (helps diagnose mobile issues)
      if (typeof navigator !== 'undefined') {
        const conn =
          navigator.connection ||
          navigator.mozConnection ||
          navigator.webkitConnection;
        if (conn) {
          event.contexts = {
            ...event.contexts,
            network: {
              type: conn.type, // wifi, cellular, etc.
              effective_type: conn.effectiveType, // 4g, 3g, 2g, slow-2g
              downlink: conn.downlink, // Mbps estimate
              rtt: conn.rtt, // Round-trip time in ms
              save_data: conn.saveData, // Data saver enabled
            },
          };
          event.tags = {
            ...event.tags,
            connection_type: conn.effectiveType || conn.type || 'unknown',
          };
        }
        event.tags = {
          ...event.tags,
          online: String(navigator.onLine),
        };
      }

      return event;
    },
    // Ignore errors originating from third-party scripts
    denyUrls: [
      /pinimg\.com/,
      /facebook\.net/,
      /googletagmanager\.com/,
      /google-analytics\.com/,
      /tiktok\.com/,
      /everflow\.io/,
      /clarity\.ms/,
    ],
    // Apply environment-specific settings
    ...envConfig,
  });
};

// Optional: Add custom context information
export const setUserContext = (user) => {
  Sentry.setUser({
    email: user.email,
  });
};
